import { arrayMove } from "@dnd-kit/sortable";
import { BasketLayerDisplayOrder } from "src/store/api/generatedApi";
import { BasketLayerState, LayerItem } from "./types";

const BASKET_DROP_PREFIX = "basket-";

export const basketDropId = (basketNumber: number): string =>
  `${BASKET_DROP_PREFIX}${basketNumber.toString()}`;

export const visualLayers = <Layer>(
  layers: Layer[],
  displayOrder: BasketLayerDisplayOrder
): Layer[] =>
  displayOrder === "bottom_first" ? [...layers].reverse() : layers;

export const basketEndLabelKeys = (
  displayOrder: BasketLayerDisplayOrder
): {
  first: "basketTop" | "basketBottom";
  last: "basketTop" | "basketBottom";
} =>
  displayOrder === "bottom_first"
    ? { first: "basketTop", last: "basketBottom" }
    : { first: "basketBottom", last: "basketTop" };

const findLayer = (baskets: BasketLayerState[], layerId: string) => {
  const basketIndex = baskets.findIndex((basket) =>
    basket.layers.some((layer) => layer.id === layerId)
  );
  const layerIndex = baskets[basketIndex]?.layers.findIndex(
    (layer) => layer.id === layerId
  );
  return layerIndex === undefined ? null : { basketIndex, layerIndex };
};

export const updateLayer = ({
  baskets,
  layerId,
  update,
}: {
  baskets: BasketLayerState[];
  layerId: string;
  update: Partial<Pick<LayerItem, "materialName" | "weight">>;
}): BasketLayerState[] =>
  baskets.map((basket) => ({
    ...basket,
    layers: basket.layers.map((layer) =>
      layer.id === layerId ? { ...layer, ...update } : layer
    ),
  }));

export const deleteLayer = ({
  baskets,
  layerId,
}: {
  baskets: BasketLayerState[];
  layerId: string;
}): BasketLayerState[] =>
  baskets.map((basket) => ({
    ...basket,
    layers: basket.layers.filter((layer) => layer.id !== layerId),
  }));

export const addLayer = ({
  baskets,
  basketNumber,
  layer,
}: {
  baskets: BasketLayerState[];
  basketNumber: number;
  layer: LayerItem;
}): BasketLayerState[] =>
  baskets.map((basket) =>
    basket.basketNumber === basketNumber
      ? { ...basket, layers: [...basket.layers, layer] }
      : basket
  );

export const splitLayer = ({
  baskets,
  layerId,
  weight1,
  weight2,
  firstLayerId,
  secondLayerId,
}: {
  baskets: BasketLayerState[];
  layerId: string;
  weight1: number;
  weight2: number;
  firstLayerId: string;
  secondLayerId: string;
}): BasketLayerState[] =>
  baskets.map((basket) => ({
    ...basket,
    layers: basket.layers.flatMap((layer) =>
      layer.id === layerId
        ? [
            { ...layer, id: firstLayerId, weight: weight1 },
            { ...layer, id: secondLayerId, weight: weight2 },
          ]
        : [layer]
    ),
  }));

export const addBasket = ({
  baskets,
  basketNumber,
}: {
  baskets: BasketLayerState[];
  basketNumber: number;
}): BasketLayerState[] =>
  baskets.some((basket) => basket.basketNumber === basketNumber)
    ? baskets
    : [...baskets, { basketNumber, fullness: null, layers: [] }].sort(
        (first, second) => first.basketNumber - second.basketNumber
      );

export const removeBasket = ({
  baskets,
  basketNumber,
}: {
  baskets: BasketLayerState[];
  basketNumber: number;
}): BasketLayerState[] =>
  baskets.length <= 1
    ? baskets
    : baskets.filter(
        (basket) =>
          basket.basketNumber !== basketNumber || basket.layers.length > 0
      );

export const moveLayerByDrag = ({
  baskets,
  activeId,
  overId,
  displayOrder,
  sameBasketDrop,
}: {
  baskets: BasketLayerState[];
  activeId: string;
  overId: string;
  displayOrder: BasketLayerDisplayOrder;
  sameBasketDrop: "ignore" | "move_to_top";
}): BasketLayerState[] => {
  if (activeId === overId) return baskets;
  const source = findLayer(baskets, activeId);
  const target = findLayer(baskets, overId);
  const targetBasketIndex =
    target?.basketIndex ??
    baskets.findIndex((basket) => basketDropId(basket.basketNumber) === overId);
  if (source === null || targetBasketIndex < 0) return baskets;
  const sourceBasket = baskets[source.basketIndex];
  const targetBasket = baskets[targetBasketIndex];
  const movedLayer = sourceBasket?.layers[source.layerIndex];
  if (!sourceBasket || !targetBasket || !movedLayer) return baskets;
  if (source.basketIndex === targetBasketIndex) {
    if (target === null && sameBasketDrop === "ignore") return baskets;
    return baskets.map((basket, index) =>
      index === source.basketIndex
        ? {
            ...basket,
            layers: arrayMove(
              basket.layers,
              source.layerIndex,
              target?.layerIndex ?? basket.layers.length - 1
            ),
          }
        : basket
    );
  }
  const insertionIndex = target
    ? target.layerIndex + (displayOrder === "bottom_first" ? 1 : 0)
    : targetBasket.layers.length;
  return baskets.map((basket, index) => {
    if (index === source.basketIndex) {
      return {
        ...basket,
        layers: basket.layers.filter((layer) => layer.id !== activeId),
      };
    }
    if (index === targetBasketIndex) {
      return {
        ...basket,
        layers: [
          ...basket.layers.slice(0, insertionIndex),
          movedLayer,
          ...basket.layers.slice(insertionIndex),
        ],
      };
    }
    return basket;
  });
};

export const moveLayerVertically = ({
  baskets,
  layerId,
  direction,
  displayOrder,
}: {
  baskets: BasketLayerState[];
  layerId: string;
  direction: "up" | "down";
  displayOrder: BasketLayerDisplayOrder;
}): BasketLayerState[] => {
  const source = findLayer(baskets, layerId);
  if (!source) return baskets;
  const offset =
    (direction === "up" ? -1 : 1) * (displayOrder === "bottom_first" ? -1 : 1);
  const targetLayer =
    baskets[source.basketIndex]?.layers[source.layerIndex + offset];
  return targetLayer
    ? moveLayerByDrag({
        baskets,
        activeId: layerId,
        overId: targetLayer.id,
        displayOrder,
        sameBasketDrop: "ignore",
      })
    : baskets;
};
